/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for Admin AI Models & Providers Management (Task 11.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Standard AI Model Modalities / Types
 */
export const AiModelModalitySchema = z.enum(['text', 'image', 'video', 'speech', 'embedding', 'chat', 'stt']);
export type AiModelModality = z.infer<typeof AiModelModalitySchema>;

/**
 * Zod schema for an upstream AI Provider.
 */
export const AdminAiProviderSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique provider ID (e.g., prov_openai)' }),
    code: z.string().openapi({ description: 'Provider short identifier (e.g., openai, anthropic, gemini)' }),
    name: z.string().openapi({ description: 'Human readable provider brand name' }),
    apiBaseUrl: z.string().nullable().optional().openapi({ description: 'Upstream base endpoint URL' }),
    status: z.enum(['active', 'inactive', 'degraded']).default('active').openapi({ description: 'Operational status' }),
    isByokAllowed: z.boolean().default(true).openapi({ description: 'Whether Bring-Your-Own-Key is enabled for this provider' }),
    sortOrder: z.number().default(0).openapi({ description: 'Display ordering weight' }),
  })
  .openapi('AdminAiProvider');

export type AdminAiProvider = z.infer<typeof AdminAiProviderSchema>;

/**
 * Zod schema for an AI Model record.
 */
export const AdminAiModelSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique model ID' }),
    providerId: z.string().openapi({ description: 'Foreign key reference to AI Provider ID' }),
    providerCode: z.string().optional().openapi({ description: 'Provider short code for UI display' }),
    providerName: z.string().optional().openapi({ description: 'Provider brand name for UI display' }),
    code: z.string().openapi({ description: 'Model machine code identifier' }),
    name: z.string().openapi({ description: 'Friendly name of the model' }),
    type: AiModelModalitySchema.openapi({ description: 'Primary modality of the model' }),
    inputPricePer1k: z.number().default(0).openapi({ description: 'Raw upstream cost per 1,000 prompt tokens in USD' }),
    outputPricePer1k: z.number().default(0).openapi({ description: 'Raw upstream cost per 1,000 completion tokens in USD' }),
    creditMultiplier: z.number().default(1.0).openapi({ description: 'Multiplier applied when deducting user credits' }),
    contextWindow: z.number().default(128000).openapi({ description: 'Maximum token context window size' }),
    isActive: z.boolean().default(true).openapi({ description: 'Whether the model is enabled for user workspaces' }),
    isDefault: z.boolean().default(false).openapi({ description: 'Whether this is the default fallback model for its modality' }),
    allowedPlanIds: z.array(z.string()).default([]).openapi({ description: 'Array of subscription plan IDs with granted access' }),
  })
  .openapi('AdminAiModel');

export type AdminAiModel = z.infer<typeof AdminAiModelSchema>;

/**
 * Schema for creating a new AI model entry in the backoffice.
 */
export const CreateAiModelSchema = z
  .object({
    providerId: z.string().min(1, 'Provider ID is required').openapi({ description: 'Provider ID' }),
    code: z.string().min(1, 'Model code is required').openapi({ description: 'Unique model identifier code' }),
    name: z.string().min(1, 'Model name is required').openapi({ description: 'Friendly model name' }),
    type: AiModelModalitySchema.default('text').openapi({ description: 'Modality' }),
    inputPricePer1k: z.number().min(0).default(0).openapi({ description: 'Input price per 1k tokens' }),
    outputPricePer1k: z.number().min(0).default(0).openapi({ description: 'Output price per 1k tokens' }),
    creditMultiplier: z.number().min(0).default(1.0).openapi({ description: 'Credit deduction multiplier' }),
    contextWindow: z.number().min(0).default(128000).openapi({ description: 'Max context window tokens' }),
    isActive: z.boolean().default(true).openapi({ description: 'Active toggle' }),
    isDefault: z.boolean().default(false).openapi({ description: 'Default fallback toggle' }),
    allowedPlanIds: z.array(z.string()).optional().default([]).openapi({ description: 'Allowed plan IDs' }),
  })
  .openapi('CreateAiModelRequest');

export type CreateAiModelInput = z.infer<typeof CreateAiModelSchema>;

/**
 * Schema for updating an existing AI model configuration.
 */
export const UpdateAiModelConfigSchema = z
  .object({
    name: z.string().min(1).optional().openapi({ description: 'Updated model name' }),
    type: AiModelModalitySchema.optional().openapi({ description: 'Updated modality' }),
    inputPricePer1k: z.number().min(0).optional().openapi({ description: 'Updated input price' }),
    outputPricePer1k: z.number().min(0).optional().openapi({ description: 'Updated output price' }),
    creditMultiplier: z.number().min(0).optional().openapi({ description: 'Updated credit multiplier' }),
    contextWindow: z.number().min(0).optional().openapi({ description: 'Updated context window' }),
    isActive: z.boolean().optional().openapi({ description: 'Toggle model availability' }),
    isDefault: z.boolean().optional().openapi({ description: 'Set as default model for its modality' }),
    allowedPlanIds: z.array(z.string()).optional().openapi({ description: 'Updated plan entitlements' }),
  })
  .openapi('UpdateAiModelConfigRequest');

export type UpdateAiModelConfigInput = z.infer<typeof UpdateAiModelConfigSchema>;

/**
 * Query schema for filtering & searching AI Models list.
 */
export const AiModelQuerySchema = z.object({
  type: AiModelModalitySchema.optional().openapi({ description: 'Filter by model modality' }),
  isActive: z.enum(['true', 'false', 'all']).optional().default('all').openapi({ description: 'Filter by active status' }),
  search: z.string().optional().openapi({ description: 'Search keywords matching model name or code' }),
  providerId: z.string().optional().openapi({ description: 'Filter by upstream provider ID' }),
});

export type AiModelQuery = z.infer<typeof AiModelQuerySchema>;
